import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  ShieldCheck,
  MessageSquare,
  FileText,
  Eye,
} from "lucide-react";

/**
 * PUBLIC Twilio A2P 10DLC reviewer / compliance page.
 *
 * Purpose:
 *   Give Twilio (and any campaign auditor) a single, no-login URL that
 *   itemizes every A2P 10DLC opt-in requirement and shows exactly how
 *   SyncBook's /sms-signup flow satisfies each one. Reduces back-and-forth
 *   on future campaign submissions and directly rebuts the "forced consent"
 *   rejection (Error 30923) by documenting the multi-path account-creation
 *   flow.
 */

function ComplianceRow({ requirement, satisfied, evidence, testid }) {
  return (
    <div
      className="nb-card p-4 space-y-2"
      data-testid={testid}
      style={{ background: "#fff" }}
    >
      <div className="flex items-start gap-2">
        <div
          className="rounded-full p-1 mt-0.5"
          style={{ background: "var(--positive)" }}
        >
          <CheckCircle2 size={14} strokeWidth={3} />
        </div>
        <div className="flex-1">
          <div className="font-display font-black text-sm">{requirement}</div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#4a4a4a] mt-0.5">
            Satisfied by
          </div>
        </div>
      </div>
      <div className="text-[13px] font-medium leading-snug pl-8">{satisfied}</div>
      <div
        className="pl-8 text-[11px] font-medium text-[#4a4a4a] italic border-l-2 border-[#1a1a1a]/10 ml-2"
        style={{ paddingLeft: "1.5rem" }}
      >
        {evidence}
      </div>
    </div>
  );
}

/** Mockup preview showing the collapsed-by-default vs expanded state.  Uses
 *  the same visual language as the real /sms-signup page so reviewers can
 *  correlate what they'll see live. */
function StatePreview({ variant, title, subtitle, children, testid }) {
  return (
    <div
      className="nb-card p-3 space-y-2 text-xs"
      data-testid={testid}
      style={{ background: variant === "default" ? "#f4f0eb" : "#fff5e6" }}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
        {title}
      </div>
      <div className="text-[12px] font-medium">{subtitle}</div>
      <div className="border-2 border-dashed border-[#1a1a1a]/30 rounded-lg p-2 space-y-1.5 bg-white">
        {children}
      </div>
    </div>
  );
}

export default function SmsReview() {
  return (
    <div
      className="max-w-4xl mx-auto p-5 space-y-6 pb-16"
      data-testid="sms-review-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/"
          data-testid="sms-review-back"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Home
        </Link>
        <div className="text-xs font-bold text-[#4a4a4a] uppercase tracking-wider">
          Compliance evidence
        </div>
        <div className="w-[70px]" />
      </div>

      {/* Purpose banner */}
      <div
        className="nb-card-lg p-6 space-y-3"
        style={{ background: "#d9d4f1" }}
        data-testid="sms-review-purpose"
      >
        <div className="inline-flex items-center gap-2 nb-tag !text-xs">
          <ShieldCheck size={12} strokeWidth={3} /> For Twilio A2P 10DLC reviewers
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl">
          SyncBook SMS opt-in — compliance summary
        </h1>
        <p className="text-sm font-medium text-[#1a1a1a]">
          This page documents how the public opt-in page at{" "}
          <Link
            to="/sms-signup"
            data-testid="sms-review-signup-link"
            className="underline font-black"
          >
            /sms-signup
          </Link>{" "}
          satisfies each A2P 10DLC requirement. Every claim below can be
          verified live at that URL without logging in. This page was created
          in response to Twilio Error 30923 (forced consent) to document that
          SMS opt-in is now entirely optional and clearly separated from
          account creation.
        </p>
      </div>

      {/* CRITICAL: response to 30923 rejection */}
      <div
        className="nb-card p-4 space-y-2"
        style={{ background: "#fdecc8" }}
        data-testid="sms-review-30923-response"
      >
        <div className="font-display font-black text-base flex items-center gap-2">
          <FileText size={16} strokeWidth={3} /> Response to Error 30923
          (forced consent)
        </div>
        <div className="text-[13px] font-medium leading-snug">
          Twilio flagged the previous <code>/sms-signup</code> page because both
          the phone number field and the consent checkbox were required to
          progress to account creation. That has been fixed. The current page:
        </div>
        <ul className="list-disc pl-6 text-[12px] font-medium space-y-1">
          <li>
            Offers a primary green button labeled{" "}
            <b>&ldquo;Create account without SMS&rdquo;</b> that is enabled on
            page load, requires no phone number, requires no consent checkbox,
            and creates a fully functional SyncBook account.
          </li>
          <li>
            Keeps the phone input, TCPA consent checkbox, and SMS-submit button{" "}
            <b>collapsed and hidden by default</b>. They render only after the
            visitor actively checks a separate opt-in toggle.
          </li>
          <li>
            Un-checking the opt-in toggle collapses the section AND clears any
            values entered — visitors can freely change their mind at any point.
          </li>
          <li>
            Displays a blue &ldquo;SMS is 100% optional&rdquo; disclosure banner
            above the opt-in section, restating that every SyncBook feature
            works without SMS.
          </li>
        </ul>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-3" data-testid="sms-review-requirements">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} strokeWidth={3} />
          <div className="font-display font-black text-lg">
            A2P 10DLC requirement checklist
          </div>
        </div>

        <ComplianceRow
          testid="req-optional"
          requirement="1. SMS opt-in must be optional and consumers can decline while still using the service."
          satisfied="A green primary CTA titled 'Create account without SMS' is the visually dominant path on the page. It requires zero form input, does not collect a phone number, and creates a fully functional SyncBook account. Every SyncBook feature (transactions, budgets, reconcile, forecasting, imports, receipts) is available on that account without SMS ever being enabled."
          evidence="Visible at /sms-signup — data-testid: skip-sms-create-account. Button is enabled on page load with no prerequisites."
        />

        <ComplianceRow
          testid="req-consent-explicit"
          requirement="2. Consent must be explicit and unchecked by default."
          satisfied="The TCPA consent checkbox defaults to unchecked and only becomes visible after the visitor proactively checks a separate 'Optional — also enable text-in transactions' toggle. Even after expanded, the submit button remains disabled until both a phone number is entered AND the TCPA checkbox is manually checked."
          evidence="React initial state: consent=false, wantsSms=false. Submit disabled attribute: !phone.trim() || !consent."
        />

        <ComplianceRow
          testid="req-single-purpose"
          requirement="3. Consent must be specific to the campaign use case."
          satisfied="The consent copy explicitly states: 'I agree to receive transactional SMS from SyncBook at the number above'. It describes exactly the message types (transaction confirmations and balance summaries), does not lump in marketing or third-party use, and clarifies that SyncBook only replies to messages initiated by the user."
          evidence="Consent copy near data-testid: sms-signup-consent-wrap."
        />

        <ComplianceRow
          testid="req-msg-frequency"
          requirement="4. Message frequency must be disclosed."
          satisfied="Consent copy discloses: 'Message frequency varies (typically 1–5 msgs/day). Message and data rates may apply.'"
          evidence="Within the consent checkbox label at /sms-signup."
        />

        <ComplianceRow
          testid="req-stop-help"
          requirement="5. STOP and HELP instructions must be present."
          satisfied="Consent copy states: 'Reply STOP to unsubscribe or HELP for help at any time.' Backend also supports STOP and HELP as first-class inbound SMS commands with immediate carrier-compliant auto-replies."
          evidence="Consent copy at /sms-signup + SMS command handler in server.py."
        />

        <ComplianceRow
          testid="req-privacy-policy"
          requirement="6. Privacy Policy and Terms links must be present and functional."
          satisfied="Both links appear inside the consent copy and route to standalone public pages at /privacy and /terms."
          evidence={
            <>
              <Link
                to="/privacy"
                data-testid="sms-review-privacy-link"
                className="underline font-bold"
              >
                Open Privacy Policy →
              </Link>
              {" · "}
              <Link
                to="/terms"
                data-testid="sms-review-terms-link"
                className="underline font-bold"
              >
                Open Terms of Service →
              </Link>
            </>
          }
        />

        <ComplianceRow
          testid="req-no-sharing"
          requirement="7. Consumer information must not be sold or shared with third parties for messaging or marketing."
          satisfied="Consent copy explicitly states: 'My phone number will not be shared with third parties.' The reviewer-facing bullets on /sms-signup also explicitly declare 'Will not: sell or share your phone number with any third party.'"
          evidence="Consent copy + reviewer bullets at data-testid: sms-signup-details."
        />
      </div>

      {/* Visual state preview */}
      <div className="space-y-3" data-testid="sms-review-state-preview">
        <div className="flex items-center gap-2">
          <Eye size={18} strokeWidth={3} />
          <div className="font-display font-black text-lg">
            What a reviewer sees at /sms-signup
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <StatePreview
            variant="default"
            title="State 1 — On page load (default)"
            subtitle="Phone/consent are NOT rendered. Primary action requires no input."
            testid="state-preview-default"
          >
            <div
              className="rounded p-1.5 font-bold text-[11px] text-center"
              style={{ background: "var(--positive)", border: "2px solid #1a1a1a" }}
            >
              [Green button] Create account without SMS →
            </div>
            <div
              className="rounded p-1.5 text-[10px] flex items-center gap-1"
              style={{ background: "#f4f0eb" }}
            >
              <span className="inline-block w-3 h-3 border-2 border-[#1a1a1a] rounded-sm" />
              <span>Optional — also enable text-in transactions</span>
            </div>
            <div className="text-[9px] italic text-[#4a4a4a] pl-4">
              (phone field, consent, submit are HIDDEN below this line)
            </div>
          </StatePreview>

          <StatePreview
            variant="expanded"
            title="State 2 — After user opts in"
            subtitle="Only if visitor actively checks the opt-in toggle."
            testid="state-preview-expanded"
          >
            <div
              className="rounded p-1.5 font-bold text-[11px] text-center"
              style={{ background: "var(--positive)", border: "2px solid #1a1a1a" }}
            >
              [Green button] Create account without SMS →
            </div>
            <div
              className="rounded p-1.5 text-[10px] flex items-center gap-1"
              style={{ background: "#fff5e6" }}
            >
              <span
                className="inline-block w-3 h-3 border-2 border-[#1a1a1a] rounded-sm"
                style={{ background: "#1a1a1a" }}
              />
              <span>Optional — also enable text-in transactions ✓</span>
            </div>
            <div className="rounded p-1.5 text-[10px] border border-[#1a1a1a]/30">
              Phone: [empty text input]
            </div>
            <div className="rounded p-1.5 text-[10px] flex items-center gap-1 border border-[#1a1a1a]/30">
              <span className="inline-block w-3 h-3 border-2 border-[#1a1a1a] rounded-sm" />
              <span>TCPA consent (starts UNCHECKED)</span>
            </div>
            <div
              className="rounded p-1.5 text-[10px] text-center"
              style={{ background: "#e0e0e0", color: "#999" }}
            >
              [Disabled] Create account WITH SMS enabled →
            </div>
          </StatePreview>
        </div>

        <div className="text-[11px] font-medium text-[#4a4a4a] italic text-center">
          Both states are verifiable live at{" "}
          <Link
            to="/sms-signup"
            className="underline font-bold"
            data-testid="sms-review-verify-link"
          >
            /sms-signup
          </Link>
          .
        </div>
      </div>

      {/* Sample messages */}
      <div
        className="nb-card p-4 space-y-2"
        data-testid="sms-review-samples"
      >
        <div className="font-display font-black text-base flex items-center gap-2">
          <MessageSquare size={16} strokeWidth={3} /> Sample messages consumers
          will receive
        </div>
        <div className="text-[11px] font-medium text-[#4a4a4a]">
          SyncBook only replies to inbound messages initiated by the consumer.
          Every outbound message body used by the program:
        </div>
        <ul
          className="text-[12px] font-mono space-y-1 pl-4 list-disc"
          data-testid="sms-review-sample-list"
        >
          <li>
            <b>Confirmation:</b> OK 1: -4.50 coffee | Bal 233.50
          </li>
          <li>
            <b>Chained confirmation:</b> OK 2: -4.50 coffee, -12 lunch | Bal 217.50
          </li>
          <li>
            <b>Balance digest:</b> Main Checking: $233.50 · Savings: $500.00
          </li>
          <li>
            <b>Recent digest:</b> Last 3: 2026-02-22: -$4.50 coffee ...
          </li>
          <li>
            <b>HELP reply:</b> SyncBook: &apos;-4.50 coffee&apos;, &apos;+150 pay&apos;. Chain w/ &apos;;&apos;: &apos;-4.50 coffee;-12 lunch&apos;. balance | recent | menu | acct:name | force
          </li>
          <li>
            <b>STOP reply (via Twilio):</b> You have successfully been unsubscribed. You will not receive any more messages from this number. Reply START to resubscribe.
          </li>
          <li>
            <b>Unregistered-phone reply:</b> This number isn&apos;t linked to a SyncBook account. Log in and add your phone under Profile → SMS.
          </li>
        </ul>
        <p className="text-[11px] font-medium text-[#4a4a4a] italic pt-1">
          Full annotated versions of each sample (with the user&apos;s inbound
          message shown side-by-side) are in{" "}
          <Link
            to="/terms"
            data-testid="sms-review-samples-terms-link"
            className="underline font-bold"
          >
            /terms § 13 (Sample messages)
          </Link>
          .
        </p>
      </div>

      {/* External links */}
      <div
        className="nb-card p-4 grid sm:grid-cols-4 gap-2"
        data-testid="sms-review-links"
      >
        <Link
          to="/sms-signup"
          data-testid="sms-review-link-signup"
          className="nb-btn nb-btn-secondary !py-2 text-sm justify-center"
        >
          <ExternalLink size={14} strokeWidth={3} /> /sms-signup
        </Link>
        <Link
          to="/sms-terms"
          data-testid="sms-review-link-sms-terms"
          className="nb-btn nb-btn-secondary !py-2 text-sm justify-center"
        >
          <ExternalLink size={14} strokeWidth={3} /> /sms-terms
        </Link>
        <Link
          to="/privacy"
          data-testid="sms-review-link-privacy"
          className="nb-btn nb-btn-secondary !py-2 text-sm justify-center"
        >
          <ExternalLink size={14} strokeWidth={3} /> /privacy
        </Link>
        <Link
          to="/terms"
          data-testid="sms-review-link-terms"
          className="nb-btn nb-btn-secondary !py-2 text-sm justify-center"
        >
          <ExternalLink size={14} strokeWidth={3} /> /terms
        </Link>
      </div>

      <p
        className="text-[11px] font-medium text-[#4a4a4a] text-center"
        data-testid="sms-review-footer"
      >
        Questions? Contact hello@sync-book.app. Last updated Feb 2026.
      </p>
    </div>
  );
}
